import { Box, Text, WizardStepRenderParams } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import countryList from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'
import { getCurrencyCode } from 'modules/core/lib/money'

import contractVersions from 'modules/contracts/store/collections/contractVersions'

import CurrencySelector from './components/payrollPolicyStep/CurrencySelector'
import EmployeesSelector from './components/payrollPolicyStep/EmployeesSelector'
import PeriodicitySelector from './components/payrollPolicyStep/PeriodicitySelector'
import { WizardData } from './types'

export const PayrollPoliceStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  useEffect(() => {
    track(trackingEvents.payroll.userLandsInPayrollPolicyOnboarding, {
      companyId,
    })
  }, [companyId])

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const countriesList = countryList[i18n.locale as keyof typeof countryList] || countryList.en

  return (
    <Box flexDirection="row" position="relative" justifyContent="center">
      <Box width="full" maxWidth="s600">
        <Box flexDirection="column" gap="s8">
          <Text.Header1 weight="semibold">
            {i18n.t('payroll_bundle.onboarding.payroll_policy_step.title')}
          </Text.Header1>

          <Text.Normal>
            {i18n.t('payroll_bundle.onboarding.payroll_policy_step.sub_headline')}
          </Text.Normal>
        </Box>

        <Box gap="s16" paddingTop="s32" paddingBottom="s32" position="relative">
          <Box aria-label={i18n.t('payroll_bundle.accessibility.policy_name')}>
            <TextField
              label={i18n.t('payroll_bundle.onboarding.payroll_policy_step.name_input')}
              value={data.name}
              onChange={(value) => {
                onDataChanged({
                  ...data,
                  name: value,
                })
              }}
            />
          </Box>

          <Box aria-label={i18n.t('payroll_bundle.accessibility.country_selector')}>
            <SelectField
              label={i18n.t('address_input.country')}
              onChange={(value) => {
                const currency = getCurrencyCode(value)
                if (currency) {
                  onDataChanged({
                    ...data,
                    country: value,
                    currency,
                    employees_selected: [],
                  })
                }
              }}
              value={data.country ?? ''}
              options={countriesList}
              none={i18n.t('common.choose')}
            />
          </Box>

          <Box aria-label={i18n.t('payroll_bundle.accessibility.currency_selector')}>
            <CurrencySelector
              currencyValue={data.currency}
              onChange={(value) => onDataChanged({ ...data, currency: value })}
            />
          </Box>

          <Box aria-label={i18n.t('payroll_bundle.accessibility.periodicity_selector')}>
            <PeriodicitySelector
              periodicityValue={data.periodicity}
              startOnValue={data.starts_on}
              onChangeStartDate={(value) => {
                onDataChanged({
                  ...data,
                  starts_on: value,
                })
              }}
              onChangePeriodicity={(value) => {
                onDataChanged({
                  ...data,
                  recurrence: value.recurrence,
                  periodicity: value.periodicity,
                  starts_on: value.starts_on,
                })
              }}
            />
          </Box>

          <Box gap="s4" aria-label={i18n.t('payroll_bundle.accessibility.employee_selector')}>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('payroll_bundle.onboarding.payroll_policy_step.employees_selector')}
            </Text.Normal>

            <EmployeesSelector
              onChange={(value) => {
                onDataChanged({ ...data, employees_selected: value })
              }}
              country={data.country}
              currency={data.currency}
              selectedEmployeeIds={data.employees_selected || []}
              filterProps={{
                hideFilterByStatus: false,
                showFilterByTeam: true,
                showFilterByLegalEntity: true,
              }}
              contractVersions={contractVersions}
            />
          </Box>
        </Box>
      </Box>
      <Box
        position="relative"
        maxWidth="s400"
        width="full"
        padding="s12"
        style={{
          top: '524px',
        }}
      >
        <Callout
          title={i18n.t('payroll_bundle.onboarding.payroll_policy_step.employees_selector_title')}
          type="info"
          description={i18n.t('payroll_bundle.onboarding.payroll_policy_step.form_tips.employees')}
        />
      </Box>
    </Box>
  )
}

export const validatePayrollStep = (data: WizardData): boolean => {
  if (
    data.country !== '' &&
    data.currency !== '' &&
    data.recurrence !== '' &&
    data.starts_on !== '' &&
    data.employees_selected.length > 0
  ) {
    return true
  }

  return false
}
